import { useAuth } from "@shared";
import { AccountLinkingForm } from "@accounts/forms/GoogleLinkingForm";
import { AccountPasswordForm } from "@accounts/forms/AccountPasswordForm";
import { MfaSetupForm } from "./MfaSetupForm";
import { TrustedDevicesList } from "./TrustedDevicesList";
import { Badge } from "@printtat/ui";
import { ShieldCheck } from "lucide-react";

export const AuthenticationSettings = () => {
  const { user, checkAuth } = useAuth();

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Security & Authentication */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <ShieldCheck className="w-4 h-4 text-gray-700" />
          <h2 className="text-sm font-semibold text-gray-900">
            Security & Authentication
          </h2>
        </div>

        {/* Status badges */}
        <div className="grid grid-cols-3 gap-4 mb-5 pb-5 border-b border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Password
            </label>
            <Badge
              color={user.password ? "green" : "gray"}
              variant="soft"
            >
              {user.password ? "Enabled" : "Not Set"}
            </Badge>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Google OAuth
            </label>
            <Badge
              color={user.googleId ? "green" : "gray"}
              variant="soft"
            >
              {user.googleId ? "Linked" : "Not Linked"}
            </Badge>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Email Verification
            </label>
            <Badge
              color={user.isVerified ? "green" : "yellow"}
              variant="soft"
            >
              {user.isVerified ? "Verified" : "Pending"}
            </Badge>
          </div>
        </div>

        {/* Password section */}
        <div className="mb-5 pb-5 border-b border-gray-100">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Password
          </h3>
          <AccountPasswordForm user={user} />
        </div>

        {/* Google account linking */}
        <div className="mb-5 pb-5 border-b border-gray-100">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Connected Accounts
          </h3>
          <AccountLinkingForm user={user} checkAuth={checkAuth} />
        </div>

        {/* Two-factor authentication (Phase 4) */}
        <div className={user.totpEnabled ? "mb-5 pb-5 border-b border-gray-100" : ""}>
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Two-Factor Authentication
          </h3>
          <MfaSetupForm
            totpEnabled={user.totpEnabled ?? false}
            onChange={() => {
              void checkAuth();
            }}
          />
        </div>

        {/* Trusted devices (Phase 4) — only relevant once MFA is enabled. */}
        {user.totpEnabled && (
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Trusted Devices
            </h3>
            <TrustedDevicesList />
          </div>
        )}
      </div>
    </div>
  );
};
